import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from "@/components/UI/card";
import { Input } from "@/components/UI/input";
import { Button } from "@/components/UI/button";
import { useToast } from "@/components/UI/use-toast";
import { loamsApi } from '@/services/api';
import { 
  Loader2, 
  FileDown, 
  Search, 
  Filter, 
  Calendar, 
  DollarSign, 
  AlertTriangle,
  Download,
  CheckCircle2,  // Add this
  XCircle,       // Add this
  Info           // Add this
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/UI/table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/UI/popover";
import { DatePicker } from "@/components/UI/date-picker";

// Helper function to format currency
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '-';
  return new Intl.NumberFormat('en-AE', {
    style: 'currency',
    currency: 'AED',
    minimumFractionDigits: 2
  }).format(amount);
};

// Helper function to format date time in Dubai time zone
const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    // Format the date in Dubai time (UTC+4)
    return new Intl.DateTimeFormat('en-AE', {
      timeZone: 'Asia/Dubai',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).format(date);
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateStr;
  }
};

// Fetch logs based on filters
// Fetch logs based on filters

// Enhanced Toast Component
const EnhancedToast = ({ title, description, status = "success", duration = 5000 }) => {
  const icons = {
    success: <CheckCircle2 className="h-5 w-5 text-green-600" />,
    error: <XCircle className="h-5 w-5 text-red-600" />,
    info: <Info className="h-5 w-5 text-blue-600" />,
    warning: <AlertTriangle className="h-5 w-5 text-amber-600" />,
    loading: <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
  };

  const styles = {
    success: "bg-green-50 border-l-4 border-green-500",
    error: "bg-red-50 border-l-4 border-red-500",
    info: "bg-blue-50 border-l-4 border-blue-500",
    warning: "bg-amber-50 border-l-4 border-amber-500",
    loading: "bg-gray-50 border-l-4 border-blue-500"
  };

  return {
    title,
    description,
    action: icons[status],
    duration,
    className: styles[status]
  };
};

const InvoiceAmountMismatchLogs = () => {
  const { toast } = useToast();
  
  // State for logs
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    invoiceNumber: '',
    startDate: null,
    endDate: null
  });
  
  
  // Get current date for default end date
  const today = new Date();
  const oneMonthAgo = new Date(today);
  oneMonthAgo.setMonth(today.getMonth() - 1);
  
  
  
  // Fetch logs based on filters
  const fetchLogs = async () => {
    try {
      setIsLoading(true);
      
      // Prepare filter parameters
      const params = {};
      if (filters.invoiceNumber) {
        params.invoice_number = filters.invoiceNumber;
      }
      if (filters.startDate) {
        params.start_date = filters.startDate.toISOString().split('T')[0];
      }
      if (filters.endDate) {
        params.end_date = filters.endDate.toISOString().split('T')[0];
      }
      
      const response = await loamsApi.getAmountMismatchLogs(params);
      console.log('API Response:', response);
      
      // Check if we have data and it contains logs
      if (response && response.data) {
        console.log('Setting logs:', response.data.logs);
        setLogs(response.data.logs || []);
        
        toast({
          title: "Logs Loaded Successfully",
          description: `Found ${response.data.logs?.length || 0} amount mismatch entries`,
          status: "success"
        });
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast({
        title: "Failed to Load Logs",
        description: error.message || "An error occurred while fetching logs",
        status: "error"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch logs on component mount
  useEffect(() => {
    fetchLogs();
  }, []);
  
  // Handle filter changes
  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };
  
  // Handle date changes
  const handleDateChange = (field, date) => {
    setFilters({
      ...filters,
      [field]: date
    });
  };
  
  // Export logs to Excel
  const exportLogs = async () => {
    try {
      setIsExporting(true);
      
      // Prepare filter parameters
      const params = {};
      if (filters.invoiceNumber) {
        params.invoiceNumber = filters.invoiceNumber;
      }
      if (filters.startDate) {
        params.startDate = filters.startDate.toISOString().split('T')[0];
      }
      if (filters.endDate) {
        params.endDate = filters.endDate.toISOString().split('T')[0];
      }
      
      const response = await loamsApi.exportAmountMismatchLogs(params);
      
      toast({
        title: "Logs Loaded Successfully",
        description: `Found ${response.data.logs.length} amount mismatch entries`,
        status: "success"
      });
    } catch (error) {
      console.error('Error exporting logs:', error);
      toast(EnhancedToast({
        title: "Export Failed",
        description: error.message || "An error occurred while exporting logs",
        status: "error"
      }));
    } finally {
      setIsExporting(false);
    }
  };
  
  // Reset filters
  const resetFilters = () => {
    setFilters({
      invoiceNumber: '',
      startDate: null,
      endDate: null
    });
  };
  
  return (
    <div className="space-y-6">
      <Card className="shadow-lg border-0 rounded-lg overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-amber-50 to-white border-b p-6">
          <div className="flex items-center space-x-5">
            <div className="h-12 w-12 bg-amber-100 rounded-full flex items-center justify-center">
              <DollarSign className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <CardTitle className="text-2xl font-bold text-amber-800">
                Invoice Amount Mismatch Logs
              </CardTitle>
              <CardDescription className="text-gray-600 text-base mt-1">
                Track and analyze payment amount discrepancies across invoices
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-6">
          {/* Filters */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex flex-wrap gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Number</label>
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-2.5 text-gray-400" />
                  <Input 
                    name="invoiceNumber"
                    value={filters.invoiceNumber}
                    onChange={handleFilterChange}
                    placeholder="LOAMS-1234"
                    className="pl-10 w-64"
                  />
                </div>
              </div>

              
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-64 justify-start text-left font-normal border-gray-300 hover:bg-gray-50"
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {filters.startDate ? (
                        filters.startDate.toLocaleDateString()
                      ) : (
                        <span className="text-gray-400">Select start date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <DatePicker
                      mode="single"
                      selected={filters.startDate}
                      onSelect={(date) => handleDateChange('startDate', date)}
                      captionLayout="dropdown-buttons"
                      fromYear={2020}
                      toYear={2030}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-64 justify-start text-left font-normal border-gray-300 hover:bg-gray-50"
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {filters.endDate ? (
                        filters.endDate.toLocaleDateString()
                      ) : (
                        <span className="text-gray-400">Select end date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <DatePicker
                      mode="single"
                      selected={filters.endDate}
                      onSelect={(date) => handleDateChange('endDate', date)}
                      captionLayout="dropdown-buttons"
                      fromYear={2020}
                      toYear={2030}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              <div className="flex gap-2">
  <Button
    onClick={fetchLogs}
    disabled={isLoading}
    className="bg-amber-600 hover:bg-amber-700 text-white"
  >
    {isLoading ? (
      <>
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading...
      </>
    ) : (
      <>
        <Filter className="mr-2 h-4 w-4" />
        Apply Filters
      </>
    )}
  </Button>
  
  <Button
    onClick={resetFilters}
    variant="outline"
    className="text-gray-700 border-gray-300"
  >
    Reset
  </Button>
  
  <Button
    onClick={exportLogs}
    disabled={isExporting || logs.length === 0}
    variant="outline"
    className="text-green-700 border-green-300 hover:bg-green-50"
  >
    {isExporting ? (
      <>
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Exporting...
      </>
    ) : (
      <>
        <Download className="mr-2 h-4 w-4" />
        Export to Excel
      </>
    )}
  </Button>

  <Button
    onClick={() => {
      // Reset filters and fetch all logs
      setFilters({
        invoiceNumber: '',
        startDate: null,
        endDate: null
      });
      fetchLogs();
    }}
    className="bg-blue-600 hover:bg-blue-700 text-white ml-2"
  >
    Show All Logs
  </Button>
</div>
            </div>
          </div>
          
          {/* Logs Table */}
<div className="border rounded-lg overflow-hidden bg-white">
  <Table>
    <TableHeader className="bg-gray-50">
      <TableRow>
        <TableHead className="font-medium">Invoice Number</TableHead>
        <TableHead className="font-medium">Project</TableHead>
        <TableHead className="font-medium">Service Type</TableHead>
        <TableHead className="font-medium">Expected Amount</TableHead>
        <TableHead className="font-medium">Actual Amount</TableHead>
        <TableHead className="font-medium">Difference</TableHead>
        <TableHead className="font-medium">Difference %</TableHead>
        <TableHead className="font-medium">Source</TableHead>
        <TableHead className="font-medium">Created At</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {isLoading ? (
        <TableRow>
          <TableCell colSpan={9} className="text-center py-10">
            <div className="flex flex-col items-center justify-center">
              <Loader2 className="h-8 w-8 text-amber-600 animate-spin mb-2" />
              <span className="text-gray-500">Loading logs...</span>
            </div>
          </TableCell>
        </TableRow>
      ) : logs && logs.length > 0 ? (
        logs.map((log, index) => {
          // Add console log to see what data we're working with
          console.log('Log data:', log);
          
          return (
            <TableRow key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
              <TableCell className="font-medium">{log.invoice_number || '-'}</TableCell>
              <TableCell>{log.project || '-'}</TableCell>
              <TableCell>{log.service_type || '-'}</TableCell>
              <TableCell>{formatCurrency(log.expected_amount)}</TableCell>
              <TableCell>{formatCurrency(log.actual_amount)}</TableCell>
              <TableCell className={Number(log.difference) < 0 ? "text-red-600" : "text-green-600"}>
                {formatCurrency(log.difference)}
              </TableCell>
              <TableCell className={Number(log.difference_percent) < 0 ? "text-red-600" : "text-green-600"}>
                {log.difference_percent != null ? `${Number(log.difference_percent).toFixed(2)}%` : '-'}
              </TableCell>
              <TableCell>{log.source || '-'}</TableCell>
              <TableCell>{formatDateTime(log.created_at)}</TableCell>
            </TableRow>
          );
        })
      ) : (
        <TableRow>
          <TableCell colSpan={9} className="text-center py-10">
            <div className="flex flex-col items-center justify-center">
              <AlertTriangle className="h-8 w-8 text-gray-400 mb-2" />
              <span className="text-gray-500 font-medium">No amount mismatch logs found</span>
              <span className="text-gray-400 text-sm mt-1">Try adjusting your filters or checking back later</span>
            </div>
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  </Table>
</div>
          
          {/* Summary footer */}
          {logs.length > 0 && (
            <div className="mt-4 bg-amber-50 rounded-lg p-4">
              <div className="flex flex-wrap gap-8">
                <div>
                  <p className="text-sm text-gray-600">Total Records</p>
                  <p className="text-xl font-bold text-amber-800">{logs.length}</p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-600">Total Mismatch Amount</p>
                  <p className="text-xl font-bold text-amber-800">
                    {formatCurrency(logs.reduce((sum, log) => sum + (log.difference || 0), 0))}
                  </p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-600">Average Difference %</p>
                  <p className="text-xl font-bold text-amber-800">
                    {(logs.reduce((sum, log) => sum + (log.difference_percent || 0), 0) / logs.length).toFixed(2)}%
                  </p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-600">Largest Mismatch</p>
                  <p className="text-xl font-bold text-amber-800">
                    {formatCurrency(Math.max(...logs.map(log => Math.abs(log.difference || 0))))}
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InvoiceAmountMismatchLogs;